import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, UploadCloud, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ClientLicenseAlert — mounted on the client's home dashboard.
 * Only renders for role=client users whose license is not verified. Shows a
 * prominent amber/red banner explaining why wholesale pricing is locked and
 * provides an inline license number + image upload right there.
 */
export default function ClientLicenseAlert() {
  const [status, setStatus] = useState(null);
  const [licNum, setLicNum] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api
      .get("/me/license-status")
      .then((r) => {
        if (r.data?.role !== "client") { setStatus({ role: r.data?.role, license_status: null }); return; }
        setStatus(r.data);
        setLicNum(r.data.license_number || "");
      })
      .catch(() => setStatus(null));
  };
  useEffect(() => { load(); }, []);

  if (!status || status.role !== "client") return null;
  const verified = status.license_status === "verified";
  if (verified) return null; // banner disappears once approved

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|gif|webp)$/i.test(f.name)) { toast.error("License image must be JPG, PNG, GIF, or WebP"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("License image must be under 15 MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!licNum.trim()) { toast.error("License number is required"); return; }
    if (!file) { toast.error("Please attach a photo of your license"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("license_number", licNum.trim());
      fd.append("license_image", file);
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/api/me/license-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true,
      });
      toast.success("License submitted — HQ will review shortly");
      setFile(null); setPreview("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const pending  = status.license_status === "pending";
  const rejected = status.license_status === "rejected";
  const tone = pending ? "amber" : "red";
  const bg   = tone === "amber" ? "border-amber-400 bg-amber-50" : "border-red-400 bg-red-50";
  const text = tone === "amber" ? "text-amber-900" : "text-red-900";
  const eyebrow = pending ? "License pending review" : rejected ? "License rejected — please re-upload" : "License required";
  const message = pending
    ? "Your cosmetology license is on file and awaiting HQ approval. Wholesale pricing unlocks the moment it's verified."
    : rejected
    ? "HQ was unable to verify your last license upload. Please re-upload a clearer image or an updated license."
    : "Your Cosmetology License is missing or pending approval. Please upload it here to unlock wholesale pricing.";

  return (
    <Card className={`p-5 mb-4 border-2 ${bg}`} data-testid="client-license-alert">
      <div className={`flex items-start gap-3 ${text}`}>
        <div className="mt-0.5">
          {pending ? <Clock size={20} /> : <AlertTriangle size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow font-bold" data-testid="license-alert-eyebrow">{eyebrow}</p>
          <p className="mt-1 text-sm">{message}</p>
          {status.license_notes && rejected && (
            <p className="mt-1 text-xs italic opacity-80">HQ note: {status.license_notes}</p>
          )}

          <form onSubmit={submit} className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div className="sm:col-span-1">
              <Label className="text-xs">License number</Label>
              <Input
                value={licNum}
                onChange={(e) => setLicNum(e.target.value)}
                placeholder="e.g. TX-COS-123456"
                data-testid="license-alert-number"
                className="bg-white"
              />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-xs">License photo</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs border border-dashed border-current rounded-md bg-white hover:bg-white/70"
                  data-testid="license-alert-file-btn"
                >
                  <UploadCloud size={13} /> {file ? "Change" : "Choose"}
                </button>
                {preview && <img src={preview} alt="preview" className="h-9 w-12 rounded border border-current object-cover" />}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="license-alert-file-input"
              />
            </div>
            <div>
              <Button
                type="submit"
                disabled={uploading}
                className={tone === "amber" ? "bg-amber-600 hover:bg-amber-700 text-white w-full" : "bg-red-600 hover:bg-red-700 text-white w-full"}
                data-testid="license-alert-submit"
              >
                {uploading ? "Uploading…" : (pending ? "Re-upload" : "Upload license")}
              </Button>
            </div>
          </form>

          {status.license_image_url && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <CheckCircle2 size={12} /> On file:
              <a
                href={status.license_image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:opacity-80"
                data-testid="license-alert-current-link"
              >
                view current image
              </a>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
